import { useEffect, useRef } from "react"

import { useUrlLookup } from "@/hooks/use-url-lookup"
import type { LookupResult } from "@/types/contents"

interface UseUrlPrefillOptions {
  url: string
  onResult: (result: LookupResult) => void
  debounceMs?: number
}

interface UrlPrefillState {
  loading: boolean
  failed: boolean
}

/**
 * Debounced URL → form prefill. Wraps `useUrlLookup` and hands each new
 * successful result to `onResult` exactly once. Because the lookup only fires
 * on a URL change, editing other fields never re-fetches and never clobbers
 * the user's manual edits.
 */
export function useUrlPrefill({
  url,
  onResult,
  debounceMs,
}: UseUrlPrefillOptions): UrlPrefillState {
  const { result, loading, failed } = useUrlLookup(url, debounceMs)

  // Keep the callback in a ref (updated in an effect, not during render) so
  // it isn't a dependency of the apply effect — an inline callback would
  // otherwise re-run it on every render.
  const onResultRef = useRef(onResult)
  useEffect(() => {
    onResultRef.current = onResult
  }, [onResult])

  useEffect(() => {
    if (result) onResultRef.current(result)
  }, [result])

  return { loading, failed }
}
